import React, { useState } from 'react';
import {
  MegaphoneIcon,
  PaperAirplaneIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { adminAPI } from '../../services/api';

const AdminAnnouncements: React.FC = () => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSendAnnouncement = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim() || !message.trim()) {
      setError('Please fill in both title and message');
      return;
    }

    setSending(true);
    setError('');
    setSuccess(false);

    try {
      await adminAPI.createAnnouncement({ title, message });
      setSuccess(true);
      setTitle('');
      setMessage('');
      
      setTimeout(() => setSuccess(false), 5000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send announcement');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex items-center mb-2">
            <MegaphoneIcon className="h-8 w-8 text-volexx-blue-500 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">Send Announcement</h1>
          </div>
          <p className="text-gray-600">
            Send a notification to all active users on the platform
          </p>
        </div>

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-volexx-lg p-4 mb-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  Announcement sent successfully to all users!
                </p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-volexx-lg p-4 mb-6">
            <div className="flex items-center">
              <ExclamationCircleIcon className="h-5 w-5 text-red-400 mr-2" />
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-volexx-lg shadow-volexx-md p-6">
          <form onSubmit={handleSendAnnouncement}>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Announcement Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter announcement title (3-100 characters)"
                maxLength={100}
                className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500"
                disabled={sending}
              />
              <p className="mt-1 text-xs text-gray-500">{title.length}/100 characters</p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Message
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Enter your announcement message (10-1000 characters)"
                maxLength={1000}
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500"
                disabled={sending}
              />
              <p className="mt-1 text-xs text-gray-500">{message.length}/1000 characters</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-volexx-lg p-4 mb-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    <span className="font-semibold">Notice:</span> This announcement will be sent to all active users via push notification and displayed in their notifications feed.
                  </p>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={sending || !title.trim() || !message.trim()}
              className="w-full bg-volexx-blue-500 text-white font-semibold py-3 px-6 rounded-volexx hover:bg-volexx-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {sending ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </>
              ) : (
                <>
                  <PaperAirplaneIcon className="h-5 w-5 mr-2" />
                  Send Announcement
                </>
              )}
            </button>
          </form>
        </div>

        <div className="mt-6 bg-gray-100 rounded-volexx-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Tips for Effective Announcements</h3>
          <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
            <li>Keep your title clear and concise</li>
            <li>Make sure your message is relevant to all users</li>
            <li>Double-check for typos before sending</li>
            <li>Avoid sending too many announcements to prevent notification fatigue</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminAnnouncements;
